import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import { useEffect } from 'react';
import { useMe } from './api/hooks.js';
import { setWorkspaceId, useWorkspaceId } from './workspace.js';
import Landing from './pages/Landing.js';
import Login from './pages/Login.js';
import VerifyMagicLink from './pages/VerifyMagicLink.js';
import AcceptInvite from './pages/AcceptInvite.js';
import AcceptSignupInvite from './pages/AcceptSignupInvite.js';
import Signup from './pages/Signup.js';
import Privacy from './pages/Privacy.js';
import Terms from './pages/Terms.js';
import Dashboard from './pages/Dashboard.js';
import LinkDetail from './pages/LinkDetail.js';
import WorkspaceSettings from './pages/WorkspaceSettings.js';
import UserSettings from './pages/UserSettings.js';
import AppLayout from './components/AppLayout.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

function RequireAuth() {
  const { data, isLoading, isError } = useMe();
  const workspaceId = useWorkspaceId();

  useEffect(() => {
    if (!data) return;
    if (data.workspaces.length === 0) return;
    const stillMember = workspaceId
      ? data.workspaces.some((w) => w.id === workspaceId)
      : false;
    if (!stillMember) setWorkspaceId(data.workspaces[0]!.id);
  }, [data, workspaceId]);

  if (isLoading) {
    return (
      <div className="grid h-full place-items-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (isError || !data) return <Navigate to="/login" replace />;
  if (data.workspaces.length === 0) {
    return (
      <div className="mx-auto mt-24 max-w-md text-center text-sm text-slate-600">
        You aren't a member of any workspace yet. Ask an admin for an invite.
      </div>
    );
  }
  return (
    <AppLayout me={data}>
      <Outlet />
    </AppLayout>
  );
}

const router = createBrowserRouter([
  { path: '/', element: <Landing /> },
  { path: '/login', element: <Login /> },
  { path: '/auth/verify', element: <VerifyMagicLink /> },
  { path: '/accept-invite', element: <AcceptInvite /> },
  { path: '/accept-signup-invite', element: <AcceptSignupInvite /> },
  { path: '/signup', element: <Signup /> },
  { path: '/privacy', element: <Privacy /> },
  { path: '/terms', element: <Terms /> },
  {
    path: '/app',
    element: <RequireAuth />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'links/:id', element: <LinkDetail /> },
      { path: 'workspace', element: <WorkspaceSettings /> },
      { path: 'settings', element: <UserSettings /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
